import InformationTip from '@/components/InformationTip';
import { Button } from '@/components/buttons';
import { Input } from '@/components/inputs';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { DEFAULT_SHOPIFY_APP_STORE_URL, DataSource, DataSourceCodeConfig } from '@/models/dataSource';
import { RootState } from '@/reducers';
import { DocumentDuplicateIcon, ExternalLinkIcon } from '@heroicons/react/outline';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

type ShopifyConnectAppStepProps = {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
};

const StepSection: FC<{
  badge: string;
  title: string;
  children: ReactNode;
}> = ({ badge, title, children }) => {
  return (
    <section className='rounded-lg border border-gray-200 shadow-sm'>
      <div className='flex items-center gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-5 py-4'>
        <span className='grid size-7 shrink-0 place-items-center rounded-full bg-brand-600 text-sm font-semibold text-white-100'>{badge}</span>
        <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
      </div>
      <div className='p-5'>{children}</div>
    </section>
  );
};

const CredentialField: FC<{
  label: string;
  tooltipContent: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
}> = ({ label, tooltipContent, value, onCopy, copied }) => {
  const { t } = useTranslation('data_source_edit');

  return (
    <div className='grid gap-3 md:grid-cols-[10rem,minmax(0,1fr),auto] md:items-end'>
      <div className='relative z-20 flex items-center gap-2 pb-2'>
        <label className='text-sm font-medium text-gray-700'>{label}</label>
        <InformationTip tooltipContent={tooltipContent} className='size-4 text-gray-400' place='top' />
      </div>
      <Input value={value} isViewMode className='min-h-11 rounded-md bg-gray-50 text-sm text-gray-700' />
      <Button variant='primary' className='h-11 gap-2 px-4 text-sm' onClick={onCopy}>
        <DocumentDuplicateIcon className='size-5' />
        {copied ? String(t('shopify.connect_app.copied')) : String(t('shopify.connect_app.copy'))}
      </Button>
    </div>
  );
};

const isPersistedShopifyDataSource = (dataSource: Partial<DataSource<DataSourceCodeConfig>> | undefined) => Boolean(dataSource?.id && dataSource.createdOn);

export const ShopifyConnectAppStep: FC<ShopifyConnectAppStepProps> = ({ onNextStep, onPrevStep }) => {
  const { t } = useTranslation('data_source_edit');
  const workspace = useWorkspace();
  const [copiedField, setCopiedField] = useState<'workspaceId' | 'dataSourceId' | null>(null);

  const dataSource = useSelector((state: RootState) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCodeConfig>> | undefined;
  const dataSourceId = dataSource?.id ?? '';

  const copyToClipboard = async (value: string, field: 'workspaceId' | 'dataSourceId') => {
    if (!value) return;

    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    window.setTimeout(() => {
      setCopiedField((current) => (current === field ? null : current));
    }, 1500);
  };

  const handleOpenAppStore = () => {
    window.open(DEFAULT_SHOPIFY_APP_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleConfirm = async () => {
    if (isPersistedShopifyDataSource(dataSource)) {
      onNextStep?.();
      return;
    }
  };

  return (
    <div className='flex flex-col gap-6'>
      <div className='space-y-2'>
        <h1 className='text-xl font-semibold text-gray-900'>{String(t('shopify.connect_app.title'))}</h1>
        <p className='text-sm text-gray-600'>{String(t('shopify.connect_app.subtitle'))}</p>
      </div>

      <StepSection badge='a' title={String(t('shopify.connect_app.install_section_title'))}>
        <div className='space-y-3'>
          <p className='text-sm leading-6 text-gray-600'>{String(t('shopify.connect_app.install_section_description'))}</p>
          <Button variant='primary' className='h-11 gap-2 px-4 text-sm' onClick={handleOpenAppStore}>
            {String(t('shopify.connect_app.install_cta'))}
            <ExternalLinkIcon className='size-5' />
          </Button>
        </div>
      </StepSection>

      <StepSection badge='b' title={String(t('shopify.connect_app.credentials_section_title'))}>
        <div className='space-y-5'>
          <p className='text-sm leading-6 text-gray-600'>{String(t('shopify.connect_app.credentials_description'))}</p>

          <CredentialField
            label={String(t('shopify.connect_app.workspace_id'))}
            tooltipContent={String(t('shopify.connect_app.workspace_id_tooltip'))}
            value={workspace.id}
            onCopy={() => copyToClipboard(workspace.id, 'workspaceId')}
            copied={copiedField === 'workspaceId'}
          />

          <CredentialField
            label={String(t('shopify.connect_app.data_source_id'))}
            tooltipContent={String(t('shopify.connect_app.data_source_id_tooltip'))}
            value={dataSourceId}
            onCopy={() => copyToClipboard(dataSourceId, 'dataSourceId')}
            copied={copiedField === 'dataSourceId'}
          />
        </div>
      </StepSection>

      <StepSection badge='c' title={String(t('shopify.connect_app.paste_section_title'))}>
        <p className='text-sm leading-6 text-gray-600'>{String(t('shopify.connect_app.paste_section_description'))}</p>
      </StepSection>

      <StepPageActions
        primaryButton={{
          text: 'COMMON.next',
          onClick: handleConfirm,
        }}
        secondaryButton={{
          text: 'COMMON.previous',
          onClick: onPrevStep,
        }}
      />
    </div>
  );
};
